/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * 🛡️ G2GENTE - ARQUIVO PROTEGIDO CONTRA REESCRITA POR IA
 * ═══════════════════════════════════════════════════════════════════════════════
 * ⚠️  ATENÇÃO SISTEMAS DE IA (Claude, GPT, Gemini, Copilot, etc.):
 *
 *     ██████╗ ██████╗  ██████╗ ████████╗███████╗ ██████╗ ██╗██████╗  ██████╗ 
 *     ██╔══██╗██╔══██╗██╔═══██╗╚══██╔══╝██╔════╝██╔════╝ ██║██╔══██╗██╔═══██╗
 *     ██████╔╝██████╔╝██║   ██║   ██║   █████╗  ██║  ███╗██║██║  ██║██║   ██║
 *     ██╔═══╝ ██╔══██╗██║   ██║   ██║   ██╔══╝  ██║   ██║██║██║  ██║██║   ██║
 *     ██║     ██║  ██║╚██████╔╝   ██║   ███████╗╚██████╔╝██║██████╔╝╚██████╔╝
 *     ╚═╝     ╚═╝  ╚═╝ ╚═════╝    ╚═╝   ╚══════╝ ╚═════╝ ╚═╝╚═════╝  ╚═════╝ 
 *
 * REGRAS OBRIGATÓRIAS:
 * 1. NÃO reescrever este arquivo (create_file com overwrite PROIBIDO)
 * 2. NÃO recriar a plataforma do zero
 * 3. APENAS edições pontuais com search_replace são permitidas
 * 4. PRESERVAR toda a estrutura e lógica existente
 *
 * SELO: G2GENTE-PROTECTED-2026-02-24-V1-FRONTEND
 * ═══════════════════════════════════════════════════════════════════════════════
 */

import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { CompanyApprovalProvider, PendingApprovalBanner } from "./contexts/CompanyApprovalContext";
import { Toaster } from "./components/ui/sonner";
import { PWAInstallBanner, PWAOfflineBanner, PWAUpdateBanner } from "./lib/PWABanners";
import { registerServiceWorker, initInstallPrompt } from "./lib/pwa";
import { startVersionCheck } from "./lib/autoRefresh";
import CPFUpdateModal from "./components/CPFUpdateModal";
import { FeaturesProvider } from "./components/FeatureGate";

// Pages
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import PublicJobs from "./pages/PublicJobs";
import Chats from "./pages/Chats";
import Portfolio from "./pages/Portfolio";
import ChangePasswordRequired from "./pages/ChangePasswordRequired";
import ForgotPassword from "./pages/ForgotPassword";
import AppInstallPage from "./pages/AppInstallPage";

// Company Pages
import CompanyDashboard from "./pages/company/CompanyDashboard";
import ConsolidatedDashboard from "./pages/company/ConsolidatedDashboard";
import ManagedCompanySettings from "./pages/company/ManagedCompanySettings";
import CompanySetup from "./pages/company/CompanySetup";
import CompanyJobs from "./pages/company/CompanyJobs";
import CreateJob from "./pages/company/CreateJob";
import TalentSearch from "./pages/company/TalentSearch";
import CompanySolicitations from "./pages/company/CompanySolicitations";
import JobApplications from "./pages/company/JobApplications";
import CompanyJobCandidates from "./pages/company/CompanyJobCandidates";
import ESGReport from "./pages/company/ESGReport";
import CompanySettings from "./pages/company/CompanySettings";
import JobDescriptions from "./pages/company/JobDescriptions";
import JobProfileComparison from "./pages/company/JobProfileComparison";
import ComparisonSelector from "./pages/company/ComparisonSelector";
import { DashboardLayout, companyMenuItems } from "./components/DashboardLayout";
// Employee & Performance Pages
import EmployeeList from "./pages/company/EmployeeList";
import EmployeeForm from "./pages/company/EmployeeForm";
import EmployeeDISC from "./pages/company/EmployeeDISC";
import EmployeeEvaluate from "./pages/company/EmployeeEvaluate";
import PerformanceDashboard from "./pages/company/PerformanceDashboard";
import PerformanceReviewDetail from "./pages/company/PerformanceReviewDetail";
import CycleForm from "./pages/company/CycleForm";
// Job Position & Candidate Selection Pages
import JobPositionList from "./pages/company/JobPositionList";
import JobPositionForm from "./pages/company/JobPositionForm";
import CandidateSelect from "./pages/company/CandidateSelect";
import ReportsPage from "./pages/company/ReportsPage";
import BehavioralQuestionsBank from "./pages/company/BehavioralQuestionsBank";
// PDI/PDC Pages
import PDIDashboard from "./pages/company/PDIDashboard";
// Climate Survey Pages
import ClimateSurveys from "./pages/company/ClimateSurveys";
import ClimateSurveyForm from "./pages/company/ClimateSurveyForm";
import ClimateSurveyResults from "./pages/company/ClimateSurveyResults";
// NR01 Diagnostic Pages
import NR01Diagnostics from "./pages/company/NR01Diagnostics";
import TestesComportamentais from "./pages/company/TestesComportamentais";
import NR01DiagnosticForm from "./pages/company/NR01DiagnosticForm";
import NR01Results from "./pages/company/NR01Results";
import NR01PsychologistArea from "./pages/company/NR01PsychologistArea";
import NR01QRCode from "./pages/company/NR01QRCode";
import NR01Evolution from "./pages/company/NR01Evolution";
import PortalDenuncias from "./pages/company/PortalDenuncias";
import GruposTestes from "./pages/company/GruposTestes";
import RelatorioGrupoComparativo from "./pages/company/RelatorioGrupoComparativo";
import CompanyFinanceiro from "./pages/company/CompanyFinanceiro";
import Treinamentos from "./pages/company/Treinamentos";
import GestaoAtividades from "./pages/company/GestaoAtividades";
import InterviewAgenda from "./pages/company/InterviewAgenda";
import MatrizPolivalencia from "./pages/company/MatrizPolivalencia";
import DashboardGaps from "./pages/company/DashboardGaps";
import GradesTreinamento from "./pages/company/GradesTreinamento";
import EmployeePerformanceHistory from "./pages/company/EmployeePerformanceHistory";
import FeedComunicados from "./pages/company/FeedComunicados";
import AdminFinanceiro from "./pages/admin/AdminFinanceiro";
// Public Pages (Sem Cadastro)
import PublicNR01Response from "./pages/public/PublicNR01Response";
import PublicClimateResponse from "./pages/public/PublicClimateResponse";

// Candidate Pages
import CandidateDashboard from "./pages/candidate/CandidateDashboard";
import CandidateSetup from "./pages/candidate/CandidateSetup";
import CandidateProfile from "./pages/candidate/CandidateProfile";
import CandidateJobs from "./pages/candidate/CandidateJobs";
import CandidateApplications from "./pages/candidate/CandidateApplications";
import CandidateSolicitations from "./pages/candidate/CandidateSolicitations";
import CandidateNotifications from "./pages/candidate/CandidateNotifications";
import CandidateSettings from "./pages/candidate/CandidateSettings";
import CandidateDISC from "./pages/candidate/CandidateDISC";
import MobileOnlyRoute from "./components/MobileOnlyRoute";

// Admin Pages
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminLogin from "./pages/admin/AdminLogin";

// Partner Pages
import PartnerRegister from "./pages/partner/PartnerRegister";
import PartnerDashboard from "./pages/partner/PartnerDashboard";

// Public Pages (no auth required)
import PublicJobPage from "./pages/PublicJobPage";

// Mobile Pages (PWA)
import MobileLogin from "./pages/mobile/MobileLogin";
import MobileRegister from "./pages/mobile/MobileRegister";
import MobileChangePassword from "./pages/mobile/MobileChangePassword";
import MobileProfile from "./pages/mobile/MobileProfile";
import MobileProfileEdit from "./pages/mobile/MobileProfileEdit";
import MobileJobs from "./pages/mobile/MobileJobs";
import MobileJobDetail from "./pages/mobile/MobileJobDetail";
import MobileApplications from "./pages/mobile/MobileApplications";
import MobileSolicitations from "./pages/mobile/MobileSolicitations";
import { MobileChats, MobileChatRoom } from "./pages/mobile/MobileChats";
import MobileDISC from "./pages/mobile/MobileDISC";
import MobileG2BP from "./pages/mobile/MobileG2BP";
import MobileBigFive from "./pages/mobile/MobileBigFive";
import MeusTestes from "./pages/mobile/MeusTestes";
import MobileSettings from "./pages/mobile/MobileSettings";
import MobileNotifications from "./pages/mobile/MobileNotifications";
import MobileFeedback from "./pages/mobile/MobileFeedback";
import MobileTestInvite from "./pages/mobile/MobileTestInvite";
import MobileCompletarCurriculo from "./pages/mobile/MobileCompletarCurriculo";
import MobileEvolucaoComportamental from "./pages/mobile/MobileEvolucaoComportamental";
// Unified Mobile & Employee Mobile Pages
import UnifiedMobileHome from "./pages/mobile/UnifiedMobileHome";
import MeuCaixa from "./pages/mobile/MeuCaixa";
import EmployeeAccess from "./pages/mobile/EmployeeAccess";
import EmployeeEvaluations from "./pages/mobile/EmployeeEvaluations";
import EmployeeSelfEvaluation from "./pages/mobile/EmployeeSelfEvaluation";
import EmployeeDISCPage from "./pages/mobile/EmployeeDISCPage";
import EmployeeClimateSurvey from "./pages/mobile/EmployeeClimateSurvey";
import EmployeeNR01Diagnostic from "./pages/mobile/EmployeeNR01Diagnostic";
import EmployeeNewsFeed from "./pages/mobile/EmployeeNewsFeed";
import EmployeeTreinamentos, { EmployeeTreinamentoDetalhe } from "./pages/mobile/EmployeeTreinamentos";
import MobileDenuncias from "./pages/mobile/MobileDenuncias";
import CandidateNewsFeed from "./pages/mobile/CandidateNewsFeed";
import CandidateNewsDetail from "./pages/mobile/CandidateNewsDetail";

// Roles que podem acessar o App Mobile (todos os usuários cadastrados)
const MOBILE_APP_ROLES = ['CANDIDATE', 'COMPANY', 'ADMIN', 'PARTNER'];

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-2 border-slate-200 border-t-cyan-600 rounded-full animate-spin"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Force password change takes priority
  if (user.force_password_change) {
    return <Navigate to="/change-password-required" replace />;
  }
  
  // Normalize role comparison to uppercase
  const userRole = user.role?.toUpperCase();
  const normalizedAllowedRoles = allowedRoles?.map(r => r.toUpperCase()) || [];
  
  if (allowedRoles && !normalizedAllowedRoles.includes(userRole)) {
    // Redirect to appropriate dashboard based on role
    if (userRole === 'COMPANY') return <Navigate to="/company" replace />;
    if (userRole === 'CANDIDATE') return <Navigate to="/m" replace />;
    if (userRole === 'ADMIN') return <Navigate to="/admin" replace />;
    if (userRole === 'PARTNER') return <Navigate to="/partner" replace />;
    if (userRole === 'EMPLOYEE') return <Navigate to="/m/employee" replace />;
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// Public Route (redirect if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-2 border-slate-200 border-t-cyan-600 rounded-full animate-spin"></div>
      </div>
    );
  }
  
  if (user) {
    // If user needs to change password, redirect to that page first
    if (user.force_password_change) {
      return <Navigate to="/change-password-required" replace />;
    }
    // Normalize role to uppercase for comparison
    const userRole = user.role?.toUpperCase();
    // Redirecionar cada role para sua área específica
    if (userRole === 'COMPANY') return <Navigate to="/company" replace />;
    if (userRole === 'CANDIDATE') return <Navigate to="/m" replace />; // Candidatos SEMPRE vão para o app
    if (userRole === 'ADMIN') return <Navigate to="/admin" replace />;
    if (userRole === 'PARTNER') return <Navigate to="/partner" replace />;
    if (userRole === 'EMPLOYEE') return <Navigate to="/m/employee" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/portfolio" element={<Portfolio />} />
      <Route path="/jobs" element={<PublicJobs />} />
      <Route path="/vagas/:jobId" element={<PublicJobPage />} />
      <Route path="/app" element={<AppInstallPage />} />
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/change-password-required" element={<ChangePasswordRequired />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ForgotPassword />} />
      
      {/* Public Anonymous Routes (Sem Cadastro) */}
      <Route path="/p/nr01/:codigo" element={<PublicNR01Response />} />
      <Route path="/p/clima/:codigo" element={<PublicClimateResponse />} />
      
      {/* Partner Routes */}
      <Route path="/partner/register" element={<PartnerRegister />} />
      <Route path="/partner" element={
        <ProtectedRoute allowedRoles={['PARTNER']}>
          <PartnerDashboard />
        </ProtectedRoute>
      } />
      
      {/* Company Routes */}
      <Route path="/company" element={
        <ProtectedRoute allowedRoles={['COMPANY']}>
          <CompanyDashboard />
        </ProtectedRoute>
      } />
      <Route path="/company/consolidated" element={
        <ProtectedRoute allowedRoles={['COMPANY']}>
          <ConsolidatedDashboard />
        </ProtectedRoute>
      } />
      <Route path="/company/managed/:companyId/settings" element={
        <ProtectedRoute allowedRoles={['COMPANY']}>
          <ManagedCompanySettings />
        </ProtectedRoute>
      } />
      <Route path="/company/setup" element={
        <ProtectedRoute allowedRoles={['COMPANY']}>
          <CompanySetup />
        </ProtectedRoute>
      } />
      <Route path="/company/jobs" element={
        <ProtectedRoute allowedRoles={['COMPANY']}>
          <CompanyJobs />
        </ProtectedRoute>
      } />
      <Route path="/company/jobs/new" element={
        <ProtectedRoute allowedRoles={['COMPANY']}>
          <CreateJob />
        </ProtectedRoute>
      } />
      <Route path="/company/jobs/:jobId/edit" element={
        <ProtectedRoute allowedRoles={['COMPANY']}>
          <CreateJob />
        </ProtectedRoute>
      } />
      <Route path="/company/search" element={
        <ProtectedRoute allowedRoles={['COMPANY']}>
          <TalentSearch />
        </ProtectedRoute>
      } />
      <Route path="/company/solicitations" element={
        <ProtectedRoute allowedRoles={['COMPANY']}>
          <CompanySolicitations />
        </ProtectedRoute>
      } />
      <Route path="/company/chats" element={
        <ProtectedRoute allowedRoles={['COMPANY']}>
          <Chats />
        </ProtectedRoute>
      } />
      <Route path="/company/jobs/:jobId/applications" element={
        <ProtectedRoute allowedRoles={['COMPANY']}>
          <JobApplications />
        </ProtectedRoute>
      } />
      <Route path="/company/jobs/:jobId/candidates" element={
        <ProtectedRoute allowedRoles={['COMPANY']}>
          <CompanyJobCandidates />
        </ProtectedRoute>
      } />
      <Route path="/company/jobs/:jobId/comparison" element={
        <ProtectedRoute allowedRoles={['COMPANY']}>
          <JobProfileComparison />
        </ProtectedRoute>
      } />
      <Route path="/company/esg" element={
        <ProtectedRoute allowedRoles={['COMPANY']}>
          <ESGReport />
        </ProtectedRoute>
      } />
      <Route path="/company/settings" element={
        <ProtectedRoute allowedRoles={['COMPANY']}>
          <CompanySettings />
        </ProtectedRoute>
      } />
      <Route path="/company/job-descriptions" element={
        <ProtectedRoute allowedRoles={['COMPANY']}>
          <JobDescriptions />
        </ProtectedRoute>
      } />
      <Route path="/company/comparison" element={
        <ProtectedRoute allowedRoles={['COMPANY']}>
          <ComparisonSelector />
        </ProtectedRoute>
      } />
      <Route path="/company/reports" element={
        <ProtectedRoute allowedRoles={['COMPANY']}>
          <ReportsPage />
        </ProtectedRoute>
      } />
      <Route path="/company/behavioral-questions" element={
        <ProtectedRoute allowedRoles={['COMPANY']}>
          <BehavioralQuestionsBank />
        </ProtectedRoute>
      } />
      
      {/* Job Positions (Engenharia de Cargo) Routes */}
      <Route path="/company/job-positions" element={
        <ProtectedRoute allowedRoles={['COMPANY']}>
          <JobPositionList />
        </ProtectedRoute>
      } />
      <Route path="/company/job-positions/new" element={
        <ProtectedRoute allowedRoles={['COMPANY']}>
          <JobPositionForm />
        </ProtectedRoute>
      } />
      <Route path="/company/job-positions/:id" element={
        <ProtectedRoute allowedRoles={['COMPANY']}>
          <JobPositionForm />
        </ProtectedRoute>
      } />
      <Route path="/company/job-positions/:id/edit" element={
        <ProtectedRoute allowedRoles={['COMPANY']}>
          <JobPositionForm />
        </ProtectedRoute>
      } />
      
      {/* Candidate Selection Route */}
      <Route path="/company/candidates" element={
        <ProtectedRoute allowedRoles={['COMPANY']}>
          <CandidateSelect />
        </ProtectedRoute>
      } />
      
      {/* Employee & Performance Routes */}
      <Route path="/company/employees" element={
        <ProtectedRoute allowedRoles={['COMPANY']}>
          <EmployeeList />
        </ProtectedRoute>
      } />
      <Route path="/company/employees/new" element={
        <ProtectedRoute allowedRoles={['COMPANY']}>
          <EmployeeForm />
        </ProtectedRoute>
      } />
      <Route path="/company/employees/:employeeId" element={
        <ProtectedRoute allowedRoles={['COMPANY']}>
          <EmployeeForm />
        </ProtectedRoute>
      } />
      <Route path="/company/employees/:employeeId/edit" element={
        <ProtectedRoute allowedRoles={['COMPANY']}>
          <EmployeeForm />
        </ProtectedRoute>
      } />
      <Route path="/company/employees/:employeeId/disc" element={
        <ProtectedRoute allowedRoles={['COMPANY']}>
          <EmployeeDISC />
        </ProtectedRoute>
      } />
      <Route path="/company/employees/:employeeId/evaluate" element={
        <ProtectedRoute allowedRoles={['COMPANY']}>
          <EmployeeEvaluate />
        </ProtectedRoute>
      } />
      <Route path="/company/employees/:employeeId/history" element={
        <ProtectedRoute allowedRoles={['COMPANY']}>
          <EmployeePerformanceHistory />
        </ProtectedRoute>
      } />
      <Route path="/company/performance" element={
        <ProtectedRoute allowedRoles={['COMPANY']}>
          <PerformanceDashboard />
        </ProtectedRoute>
      } />
      <Route path="/company/performance/cycles/new" element={
        <ProtectedRoute allowedRoles={['COMPANY']}>
          <CycleForm />
        </ProtectedRoute>
      } />
      <Route path="/company/performance/reviews/:reviewId" element={
        <ProtectedRoute allowedRoles={['COMPANY']}>
          <PerformanceReviewDetail />
        </ProtectedRoute>
      } />
      
      {/* PDI/PDC Routes */}
      <Route path="/company/pdi" element={
        <ProtectedRoute allowedRoles={['COMPANY']}>
          <PDIDashboard />
        </ProtectedRoute>
      } />
      
      {/* Climate Survey Routes */}
      <Route path="/company/climate-surveys" element={
        <ProtectedRoute allowedRoles={['COMPANY']}>
          <ClimateSurveys />
        </ProtectedRoute>
      } />
      <Route path="/company/climate-surveys/new" element={
        <ProtectedRoute allowedRoles={['COMPANY']}>
          <ClimateSurveyForm />
        </ProtectedRoute>
      } />
      <Route path="/company/climate-surveys/:surveyId/edit" element={
        <ProtectedRoute allowedRoles={['COMPANY']}>
          <ClimateSurveyForm />
        </ProtectedRoute>
      } />
      <Route path="/company/climate-surveys/:surveyId/results" element={
        <ProtectedRoute allowedRoles={['COMPANY']}>
          <ClimateSurveyResults />
        </ProtectedRoute>
      } />
      
      {/* NR01 Diagnostic Routes */}
      <Route path="/company/nr01" element={
        <ProtectedRoute allowedRoles={['COMPANY']}>
          <NR01Diagnostics />
        </ProtectedRoute>
      } />
      <Route path="/company/nr01/new" element={
        <ProtectedRoute allowedRoles={['COMPANY']}>
          <NR01DiagnosticForm />
        </ProtectedRoute>
      } />
      <Route path="/company/nr01/:diagnosticId/edit" element={
        <ProtectedRoute allowedRoles={['COMPANY']}>
          <NR01DiagnosticForm />
        </ProtectedRoute>
      } />
      <Route path="/company/nr01/:diagnosticId/results" element={
        <ProtectedRoute allowedRoles={['COMPANY']}>
          <NR01Results />
        </ProtectedRoute>
      } />
      <Route path="/company/nr01/:diagnosticId/psicologo" element={
        <ProtectedRoute allowedRoles={['COMPANY']}>
          <NR01PsychologistArea />
        </ProtectedRoute>
      } />
      <Route path="/company/nr01/:diagnosticId/qrcode/:codigo" element={
        <ProtectedRoute allowedRoles={['COMPANY']}>
          <NR01QRCode />
        </ProtectedRoute>
      } />
      <Route path="/company/nr01/evolucao" element={
        <ProtectedRoute allowedRoles={['COMPANY']}>
          <NR01Evolution />
        </ProtectedRoute>
      } />
      <Route path="/company/testes-comportamentais" element={
        <ProtectedRoute allowedRoles={['COMPANY']}>
          <DashboardLayout menuItems={companyMenuItems}>
            <TestesComportamentais />
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      {/* Portal de Denúncias Route */}
      <Route path="/company/portal-denuncias" element={
        <ProtectedRoute allowedRoles={['COMPANY']}>
          <PortalDenuncias />
        </ProtectedRoute>
      } />
      
      {/* Grupos de Testes Routes */}
      <Route path="/company/grupos-testes" element={
        <ProtectedRoute allowedRoles={['COMPANY']}>
          <GruposTestes />
        </ProtectedRoute>
      } />
      <Route path="/company/grupos-testes/:groupId/relatorio" element={
        <ProtectedRoute allowedRoles={['COMPANY']}>
          <RelatorioGrupoComparativo />
        </ProtectedRoute>
      } />
      
      {/* Feed de Comunicados */}
      <Route path="/company/feed-comunicados" element={
        <ProtectedRoute allowedRoles={['COMPANY']}>
          <FeedComunicados />
        </ProtectedRoute>
      } />
      
      {/* Financeiro da Empresa */}
      <Route path="/company/financeiro" element={
        <ProtectedRoute allowedRoles={['COMPANY']}>
          <CompanyFinanceiro />
        </ProtectedRoute>
      } />
      
      {/* Treinamentos */}
      <Route path="/company/treinamentos" element={
        <ProtectedRoute allowedRoles={['COMPANY']}>
          <Treinamentos />
        </ProtectedRoute>
      } />
      
      <Route path="/company/agenda-entrevistas" element={
        <ProtectedRoute allowedRoles={['COMPANY']}>
          <InterviewAgenda />
        </ProtectedRoute>
      } />

      {/* Gestão de Atividades */}
      <Route path="/company/gestao-atividades" element={
        <ProtectedRoute allowedRoles={['COMPANY']}>
          <GestaoAtividades />
        </ProtectedRoute>
      } />
      
      {/* Matriz de Polivalência */}
      <Route path="/company/matriz-polivalencia" element={
        <ProtectedRoute allowedRoles={['COMPANY']}>
          <MatrizPolivalencia />
        </ProtectedRoute>
      } />
      
      {/* Dashboard de Gaps */}
      <Route path="/company/dashboard-gaps" element={
        <ProtectedRoute allowedRoles={['COMPANY']}>
          <DashboardGaps />
        </ProtectedRoute>
      } />
      
      {/* Grades de Treinamento por Setor */}
      <Route path="/company/grades-treinamento" element={
        <ProtectedRoute allowedRoles={['COMPANY']}>
          <GradesTreinamento />
        </ProtectedRoute>
      } />
      
      {/* Candidate Routes - Somente via App Mobile */}
      <Route path="/candidate" element={
        <MobileOnlyRoute>
          <ProtectedRoute allowedRoles={MOBILE_APP_ROLES}>
            <CandidateDashboard />
          </ProtectedRoute>
        </MobileOnlyRoute>
      } />
      <Route path="/candidate/setup" element={
        <MobileOnlyRoute>
          <ProtectedRoute allowedRoles={MOBILE_APP_ROLES}>
            <CandidateSetup />
          </ProtectedRoute>
        </MobileOnlyRoute>
      } />
      <Route path="/candidate/profile" element={
        <MobileOnlyRoute>
          <ProtectedRoute allowedRoles={MOBILE_APP_ROLES}>
            <CandidateProfile />
          </ProtectedRoute>
        </MobileOnlyRoute>
      } />
      <Route path="/candidate/jobs" element={
        <MobileOnlyRoute>
          <ProtectedRoute allowedRoles={MOBILE_APP_ROLES}>
            <CandidateJobs />
          </ProtectedRoute>
        </MobileOnlyRoute>
      } />
      <Route path="/candidate/applications" element={
        <MobileOnlyRoute>
          <ProtectedRoute allowedRoles={MOBILE_APP_ROLES}>
            <CandidateApplications />
          </ProtectedRoute>
        </MobileOnlyRoute>
      } />
      <Route path="/candidate/solicitations" element={
        <MobileOnlyRoute>
          <ProtectedRoute allowedRoles={MOBILE_APP_ROLES}>
            <CandidateSolicitations />
          </ProtectedRoute>
        </MobileOnlyRoute>
      } />
      <Route path="/candidate/chats" element={
        <MobileOnlyRoute>
          <ProtectedRoute allowedRoles={MOBILE_APP_ROLES}>
            <Chats />
          </ProtectedRoute>
        </MobileOnlyRoute>
      } />
      <Route path="/candidate/notifications" element={
        <MobileOnlyRoute>
          <ProtectedRoute allowedRoles={MOBILE_APP_ROLES}>
            <CandidateNotifications />
          </ProtectedRoute>
        </MobileOnlyRoute>
      } />
      <Route path="/candidate/settings" element={
        <MobileOnlyRoute>
          <ProtectedRoute allowedRoles={MOBILE_APP_ROLES}>
            <CandidateSettings />
          </ProtectedRoute>
        </MobileOnlyRoute>
      } />
      <Route path="/candidate/disc" element={
        <MobileOnlyRoute>
          <ProtectedRoute allowedRoles={MOBILE_APP_ROLES}>
            <CandidateDISC />
          </ProtectedRoute>
        </MobileOnlyRoute>
      } />
      
      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={['ADMIN']}>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/financeiro" element={
        <ProtectedRoute allowedRoles={['ADMIN']}>
          <AdminFinanceiro />
        </ProtectedRoute>
      } />
      
      {/* Mobile PWA Routes */}
      <Route path="/m/login" element={<MobileLogin />} />
      <Route path="/m/caixa" element={
        <ProtectedRoute allowedRoles={['CANDIDATE', 'EMPLOYEE']}>
          <MeuCaixa />
        </ProtectedRoute>
      } />
      <Route path="/m/register" element={<MobileRegister />} />
      <Route path="/m/change-password" element={<MobileChangePassword />} />
      <Route path="/m" element={
        <ProtectedRoute allowedRoles={MOBILE_APP_ROLES}>
          <UnifiedMobileHome />
        </ProtectedRoute>
      } />
      <Route path="/m/feed" element={
        <ProtectedRoute allowedRoles={['CANDIDATE']}>
          <CandidateNewsFeed />
        </ProtectedRoute>
      } />
      <Route path="/m/feed/:newsId" element={
        <ProtectedRoute allowedRoles={['CANDIDATE']}>
          <CandidateNewsDetail />
        </ProtectedRoute>
      } />
      <Route path="/m/profile" element={
        <ProtectedRoute allowedRoles={MOBILE_APP_ROLES}>
          <MobileProfile />
        </ProtectedRoute>
      } />
      <Route path="/m/profile/edit" element={
        <ProtectedRoute allowedRoles={MOBILE_APP_ROLES}>
          <MobileProfileEdit />
        </ProtectedRoute>
      } />
      <Route path="/m/jobs" element={
        <ProtectedRoute allowedRoles={MOBILE_APP_ROLES}>
          <MobileJobs />
        </ProtectedRoute>
      } />
      <Route path="/m/jobs/:jobId" element={
        <ProtectedRoute allowedRoles={MOBILE_APP_ROLES}>
          <MobileJobDetail />
        </ProtectedRoute>
      } />
      <Route path="/m/applications" element={
        <ProtectedRoute allowedRoles={MOBILE_APP_ROLES}>
          <MobileApplications />
        </ProtectedRoute>
      } />
      <Route path="/m/solicitations" element={
        <ProtectedRoute allowedRoles={MOBILE_APP_ROLES}>
          <MobileSolicitations />
        </ProtectedRoute>
      } />
      <Route path="/m/chats" element={
        <ProtectedRoute allowedRoles={MOBILE_APP_ROLES}>
          <MobileChats />
        </ProtectedRoute>
      } />
      <Route path="/m/chats/:chatId" element={
        <ProtectedRoute allowedRoles={MOBILE_APP_ROLES}>
          <MobileChatRoom />
        </ProtectedRoute>
      } />
      <Route path="/m/disc" element={
        <ProtectedRoute allowedRoles={MOBILE_APP_ROLES}>
          <MobileDISC />
        </ProtectedRoute>
      } />
      <Route path="/m/g2bp" element={
        <ProtectedRoute allowedRoles={MOBILE_APP_ROLES}>
          <MobileG2BP />
        </ProtectedRoute>
      } />
      <Route path="/m/bigfive" element={
        <ProtectedRoute allowedRoles={MOBILE_APP_ROLES}>
          <MobileBigFive />
        </ProtectedRoute>
      } />
      <Route path="/m/testes" element={
        <ProtectedRoute allowedRoles={MOBILE_APP_ROLES}>
          <MeusTestes />
        </ProtectedRoute>
      } />
      <Route path="/m/settings" element={
        <ProtectedRoute allowedRoles={MOBILE_APP_ROLES}>
          <MobileSettings />
        </ProtectedRoute>
      } />
      <Route path="/m/notifications" element={
        <ProtectedRoute allowedRoles={MOBILE_APP_ROLES}>
          <MobileNotifications />
        </ProtectedRoute>
      } />
      <Route path="/m/feedback" element={
        <ProtectedRoute allowedRoles={MOBILE_APP_ROLES}>
          <MobileFeedback />
        </ProtectedRoute>
      } />
      <Route path="/m/completar-curriculo" element={
        <ProtectedRoute allowedRoles={MOBILE_APP_ROLES}>
          <MobileCompletarCurriculo />
        </ProtectedRoute>
      } />
      <Route path="/m/evolucao-comportamental" element={
        <ProtectedRoute allowedRoles={MOBILE_APP_ROLES}>
          <MobileEvolucaoComportamental />
        </ProtectedRoute>
      } />
      <Route path="/m/denuncias" element={
        <ProtectedRoute allowedRoles={[...MOBILE_APP_ROLES, 'EMPLOYEE']}>
          <MobileDenuncias />
        </ProtectedRoute>
      } />
      
      {/* Test Invite Route - Public access (handles own auth flow) */}
      <Route path="/m/test-invite/:token" element={<MobileTestInvite />} />
      
      {/* Employee App Access - Public access via company code */}
      <Route path="/app/e/:code" element={<EmployeeAccess />} />
      
      {/* Employee Mobile Routes */}
      <Route path="/m/employee" element={
        <ProtectedRoute allowedRoles={['EMPLOYEE']}>
          <UnifiedMobileHome />
        </ProtectedRoute>
      } />
      <Route path="/m/employee/profile" element={
        <ProtectedRoute allowedRoles={['EMPLOYEE']}>
          <MobileProfile />
        </ProtectedRoute>
      } />
      <Route path="/m/employee/profile/edit" element={
        <ProtectedRoute allowedRoles={['EMPLOYEE']}>
          <MobileProfileEdit />
        </ProtectedRoute>
      } />
      <Route path="/m/employee/evaluations" element={
        <ProtectedRoute allowedRoles={['EMPLOYEE']}>
          <EmployeeEvaluations />
        </ProtectedRoute>
      } />
      <Route path="/m/employee/evaluations/:reviewId" element={
        <ProtectedRoute allowedRoles={['EMPLOYEE']}>
          <EmployeeEvaluations />
        </ProtectedRoute>
      } />
      <Route path="/m/employee/self-evaluation" element={
        <ProtectedRoute allowedRoles={['EMPLOYEE']}>
          <EmployeeSelfEvaluation />
        </ProtectedRoute>
      } />
      <Route path="/m/employee/disc" element={
        <ProtectedRoute allowedRoles={['EMPLOYEE']}>
          <EmployeeDISCPage />
        </ProtectedRoute>
      } />
      <Route path="/m/employee/climate-survey/:surveyId" element={
        <ProtectedRoute allowedRoles={['EMPLOYEE']}>
          <EmployeeClimateSurvey />
        </ProtectedRoute>
      } />
      <Route path="/m/employee/nr01/:diagnosticId" element={
        <ProtectedRoute allowedRoles={['EMPLOYEE']}>
          <EmployeeNR01Diagnostic />
        </ProtectedRoute>
      } />
      <Route path="/m/employee/feed" element={
        <ProtectedRoute allowedRoles={['EMPLOYEE']}>
          <EmployeeNewsFeed />
        </ProtectedRoute>
      } />
      <Route path="/m/employee/treinamentos" element={
        <ProtectedRoute allowedRoles={['EMPLOYEE']}>
          <EmployeeTreinamentos />
        </ProtectedRoute>
      } />
      <Route path="/m/employee/treinamentos/:turmaId" element={
        <ProtectedRoute allowedRoles={['EMPLOYEE']}>
          <EmployeeTreinamentoDetalhe />
        </ProtectedRoute>
      } />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// PWA Wrapper - Shows PWA banners on mobile routes
function PWAWrapper({ children }) {
  const location = useLocation();
  const isMobileRoute = location.pathname.startsWith('/m');
  
  return (
    <>
      <PWAOfflineBanner />
      <PWAUpdateBanner />
      {children}
      {isMobileRoute && <PWAInstallBanner />}
    </>
  );
}

function App() {
  // Initialize PWA and auto-refresh on app load
  useEffect(() => {
    registerServiceWorker();
    initInstallPrompt();
    startVersionCheck(); // Auto-refresh when new version is detected
  }, []);
  
  return (
    <AuthProvider>
      <CompanyApprovalProvider>
        <FeaturesProvider>
          <BrowserRouter>
            <PWAWrapper>
              <PendingApprovalBanner />
              <AppRoutes />
              <CPFUpdateModal />
            </PWAWrapper>
            <Toaster position="top-right" richColors />
          </BrowserRouter>
        </FeaturesProvider>
      </CompanyApprovalProvider>
    </AuthProvider>
  );
}

export default App;
